import React, { useState, useEffect } from 'react';
import { analyticsService } from '../../../services/api/knowledge/analyticsService';
import { GASettings } from '../../../types/analytics';
import { searchConsoleService, SearchConsoleSettings } from '../../../services/api/knowledge/searchConsoleService';
import GASettingsModal from '../../../components/knowledge/GASettingsModal';
import GADashboardModal from '../../../components/knowledge/GADashboardModal';
import SearchConsoleSettingsModal from '../../../components/knowledge/SearchConsoleSettingsModal';
import ProductAdAccountsTab from './ProductAdAccountsTab';

interface APIIntegrationTabProps {
  productId?: number;
}

const APIIntegrationTab: React.FC<APIIntegrationTabProps> = ({ productId }) => {
  const [gaSettings, setGaSettings] = useState<GASettings | null>(null);
  const [gaLoading, setGaLoading] = useState<boolean>(false);
  const [showGASettingsModal, setShowGASettingsModal] = useState<boolean>(false);
  const [showGADashboardModal, setShowGADashboardModal] = useState<boolean>(false);

  const [searchConsoleSettings, setSearchConsoleSettings] = useState<SearchConsoleSettings | null>(null);
  const [scLoading, setScLoading] = useState<boolean>(false);
  const [showSCSettingsModal, setShowSCSettingsModal] = useState<boolean>(false);

  // Load GA settings when productId changes
  useEffect(() => {
    const loadGASettings = async () => {
      if (!productId) {
        setGaSettings(null);
        return;
      }

      setGaLoading(true);
      try {
        const settings = await analyticsService.getSettings(productId);
        setGaSettings(settings);
      } catch (error) {
        console.error('Failed to load GA settings:', error);
        setGaSettings(null);
      } finally {
        setGaLoading(false);
      }
    };

    loadGASettings();
  }, [productId]);

  // Load Search Console settings when productId changes
  useEffect(() => {
    const loadSCSettings = async () => {
      if (!productId) {
        setSearchConsoleSettings(null);
        return;
      }

      setScLoading(true);
      try {
        const settings = await searchConsoleService.getSettings(productId);
        setSearchConsoleSettings(settings);
      } catch (error) {
        console.error('Failed to load Search Console settings:', error);
        setSearchConsoleSettings(null);
      } finally {
        setScLoading(false);
      }
    };

    loadSCSettings();
  }, [productId]);

  const handleGAConnect = () => {
    setShowGASettingsModal(true);
  };

  const handleGAViewDashboard = () => {
    setShowGADashboardModal(true);
  };

  const handleGAEdit = () => {
    setShowGASettingsModal(true);
  };

  const handleGASettingsSaved = async () => {
    // Reload GA settings after saving
    if (!productId) return;

    setGaLoading(true);
    try {
      const settings = await analyticsService.getSettings(productId);
      setGaSettings(settings);
    } catch (error) {
      console.error('Failed to reload GA settings:', error);
    } finally {
      setGaLoading(false);
    }
  };

  const handleSCConnect = () => {
    setShowSCSettingsModal(true);
  };

  const handleSCEdit = () => {
    setShowSCSettingsModal(true);
  };

  const handleSCSettingsSaved = async () => {
    // Reload Search Console settings after saving
    if (!productId) return;

    setScLoading(true);
    try {
      const settings = await searchConsoleService.getSettings(productId);
      setSearchConsoleSettings(settings);
    } catch (error) {
      console.error('Failed to reload Search Console settings:', error);
    } finally {
      setScLoading(false);
    }
  };

  const formatLastSyncTime = (lastSyncedAt?: string): string => {
    if (!lastSyncedAt) return '';

    const syncDate = new Date(lastSyncedAt);
    const now = new Date();
    const diffMs = now.getTime() - syncDate.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);

    if (diffMins < 1) return '1分前';
    if (diffMins < 60) return `${diffMins}分前`;
    if (diffHours < 24) return `${diffHours}時間前`;

    return syncDate.toLocaleDateString('ja-JP');
  };

  // Tasky Design System shared styles
  const serviceItemClass =
    'bg-white border border-warm-border rounded-tasky-md p-4 flex items-center justify-between gap-3 hover:shadow-card transition-shadow';
  const serviceIconClass =
    'w-10 h-10 rounded-tasky-sm bg-warm-surface flex items-center justify-center text-lg flex-shrink-0';
  const serviceNameClass = 'text-sm font-semibold text-dark';
  const serviceMetaClass = 'text-xs text-gray-400 mt-0.5';
  const editBtnClass =
    'px-3 py-1.5 text-xs font-medium bg-warm-surface text-gray-700 rounded-tasky-sm hover:bg-warm-hover transition-colors';
  const viewDetailsBtnClass =
    'px-3 py-1.5 text-xs font-medium bg-primary text-white rounded-tasky-sm hover:bg-primary-hover transition-colors';
  const connectBtnClass =
    'px-3.5 py-1.5 text-xs font-medium bg-primary text-white rounded-tasky-sm hover:bg-primary-hover transition-colors disabled:bg-gray-200 disabled:text-gray-400 disabled:cursor-not-allowed';

  const renderGACard = () => {
    const isConnected = gaSettings !== null && gaSettings.is_active;
    const lastSyncText = gaSettings?.last_synced_at ? formatLastSyncTime(gaSettings.last_synced_at) : '';
    const hasSyncError = gaSettings?.last_sync_status === 'failed';

    return (
      <div className={serviceItemClass}>
        <div className="flex items-center gap-3 min-w-0">
          <div className={serviceIconClass}>📊</div>
          <div className="min-w-0">
            <div className={serviceNameClass}>Google Analytics 4</div>
            <div className={serviceMetaClass}>
              {gaLoading ? (
                '読み込み中...'
              ) : isConnected ? (
                hasSyncError ? (
                  <span className="text-destructive">同期エラー</span>
                ) : lastSyncText ? (
                  `最終同期: ${lastSyncText}`
                ) : (
                  '接続済み'
                )
              ) : (
                'ウェブサイト分析ツール'
              )}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          {isConnected ? (
            <>
              <span
                className={`w-2 h-2 rounded-full ${hasSyncError ? 'bg-destructive' : 'bg-success'}`}
              ></span>
              <span
                className={`text-xs font-semibold ${hasSyncError ? 'text-destructive' : 'text-success'}`}
              >
                {hasSyncError ? 'Error' : 'Connected'}
              </span>
              <button className={editBtnClass} onClick={handleGAEdit}>
                編集
              </button>
              <button
                className={viewDetailsBtnClass}
                onClick={handleGAViewDashboard}
              >
                詳細を見る
              </button>
            </>
          ) : (
            <button
              className={connectBtnClass}
              onClick={handleGAConnect}
              disabled={!productId || gaLoading}
            >
              接続
            </button>
          )}
        </div>
      </div>
    );
  };

  const renderSCCard = () => {
    const isConnected = searchConsoleSettings !== null && searchConsoleSettings.is_connected;
    const lastSyncText = searchConsoleSettings?.last_sync_at ? formatLastSyncTime(searchConsoleSettings.last_sync_at) : '';

    return (
      <div className={serviceItemClass}>
        <div className="flex items-center gap-3 min-w-0">
          <div className={serviceIconClass}>🔍</div>
          <div className="min-w-0">
            <div className={serviceNameClass}>Google Search Console</div>
            <div className={serviceMetaClass}>
              {scLoading ? (
                '読み込み中...'
              ) : isConnected ? (
                lastSyncText ? (
                  `最終同期: ${lastSyncText}`
                ) : (
                  '接続済み'
                )
              ) : (
                '検索分析ツール'
              )}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          {isConnected ? (
            <>
              <span className="w-2 h-2 rounded-full bg-success"></span>
              <span className="text-xs font-semibold text-success">Connected</span>
              <button className={editBtnClass} onClick={handleSCEdit}>
                編集
              </button>
            </>
          ) : (
            <button
              className={connectBtnClass}
              onClick={handleSCConnect}
              disabled={!productId || scLoading}
            >
              接続
            </button>
          )}
        </div>
      </div>
    );
  };

  return (
    <div>
      <div>
        <div className="pb-4 mb-6 border-b border-warm-border">
          <h2 className="text-xl font-semibold text-dark">API連携</h2>
        </div>

        <div className="mb-8"><ProductAdAccountsTab productId={productId} /></div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* 接続済みのサービス */}
          {(gaSettings?.is_active || searchConsoleSettings?.is_connected) && (
            <div>
              <h3 className="text-base font-semibold text-dark pb-2 mb-4 border-b-2 border-warm-border">接続済みのサービス</h3>
              <div className="flex flex-col gap-3">
                {gaSettings?.is_active && renderGACard()}
                {searchConsoleSettings?.is_connected && renderSCCard()}
              </div>
            </div>
          )}

          {/* 利用可能なサービス */}
          <div>
            <h3 className="text-base font-semibold text-dark pb-2 mb-4 border-b-2 border-warm-border">利用可能なサービス</h3>
            <div className="flex flex-col gap-3">
              {!gaSettings?.is_active && renderGACard()}
              {!searchConsoleSettings?.is_connected && renderSCCard()}

              {/* LINE/Yahoo広告（準備中） */}
              <div className={serviceItemClass}>
                <div className="flex items-center gap-3 min-w-0">
                  <div className={serviceIconClass}>💬</div>
                  <div className="min-w-0">
                    <div className={serviceNameClass}>LINE Ads</div>
                    <div className={serviceMetaClass}>LINE広告プラットフォーム</div>
                  </div>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <button className={connectBtnClass} disabled>準備中</button>
                </div>
              </div>
              <div className={serviceItemClass}>
                <div className="flex items-center gap-3 min-w-0">
                  <div className={serviceIconClass}>🔎</div>
                  <div className="min-w-0">
                    <div className={serviceNameClass}>Yahoo! Ads</div>
                    <div className={serviceMetaClass}>Yahoo!広告プラットフォーム</div>
                  </div>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <button className={connectBtnClass} disabled>準備中</button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* GA Settings Modal */}
      {productId && (
        <GASettingsModal
          isOpen={showGASettingsModal}
          onClose={() => setShowGASettingsModal(false)}
          productId={productId}
          existingSettings={gaSettings}
          onSuccess={handleGASettingsSaved}
        />
      )}

      {/* GA Dashboard Modal */}
      {productId && (
        <GADashboardModal
          isOpen={showGADashboardModal}
          onClose={() => setShowGADashboardModal(false)}
          productId={productId}
        />
      )}

      {/* Search Console Settings Modal */}
      {productId && (
        <SearchConsoleSettingsModal
          isOpen={showSCSettingsModal}
          onClose={() => setShowSCSettingsModal(false)}
          productId={productId}
          existingSettings={searchConsoleSettings}
          onSuccess={handleSCSettingsSaved}
        />
      )}


    </div>
  );
};

export default APIIntegrationTab;
